import { Trash2, Building2, Pencil, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { useBrandsStore } from "@/stores/brands";
import { useWorkspacesStore } from "@/stores/workspaces";
import type { Brand } from "../../../types";
import { cn } from "@/lib/utils";

interface BrandsTableProps {
  onEdit?: (brand: Brand) => void;
}

export function BrandsTable({ onEdit }: BrandsTableProps) {
  const { brands, loading, error } = useBrandsStore();
  const fetch = useBrandsStore((s) => s.fetch);
  const remove = useBrandsStore((s) => s.remove);
  const { current: currentWs, update: updateWs } = useWorkspacesStore();
  const primaryBrandId = currentWs?.primary_brand_id;

  if (loading) {
    return (
      <Card>
        <div className="p-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-slate-100 rounded-md animate-subtle-pulse" />
          ))}
        </div>
      </Card>
    );
  }

  if (error && !loading) {
    return <ErrorState message={error} onRetry={fetch} />;
  }

  if (brands.length === 0) {
    return (
      <EmptyState
        icon={Building2}
        title="No brands yet"
        description="Add your first brand to start running comparisons."
      />
    );
  }

  const handleSetPrimary = async (brandId: number) => {
    if (!currentWs) return;
    const newPrimaryId = primaryBrandId === brandId ? null : brandId;
    await updateWs(currentWs.id, { primary_brand_id: newPrimaryId });
  };

  return (
    <Card className="overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Brand</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="w-36" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {brands.map((brand) => {
            const isPrimary = primaryBrandId === brand.id;
            return (
              <TableRow key={brand.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-brand-950">{brand.name}</span>
                    {isPrimary && <Badge variant="winner">Your Brand</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-slate-500">
                  {brand.description || (
                    <span className="text-slate-300">No description</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "hover:text-accent-500",
                        isPrimary ? "text-accent-500" : "text-slate-400"
                      )}
                      title={isPrimary ? "Unset as your brand" : "Mark as your brand"}
                      onClick={() => handleSetPrimary(brand.id)}
                    >
                      <Star className={cn("w-4 h-4", isPrimary && "fill-accent-500")} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-slate-400 hover:text-brand-600"
                      onClick={() => onEdit?.(brand)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-slate-400 hover:text-red-600"
                      onClick={() => {
                        if (confirm(`Delete "${brand.name}"?`)) {
                          remove(brand.id);
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}
